import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ROUTES } from "../../constant/common.constant";
import { palette } from "../../styles/theme";
import { LayerModel } from "./specsModel/LayerModel";
import type {
  SpecsCatalogFabric,
  SpecsCatalogFabricColor,
} from "./specsModel/types";
import type { SpecsLayer } from "./types";

const INHERITED_COLOR_LABEL = "Inherit";

type SidebarFabricColor = SpecsCatalogFabricColor & { name: string };

type SidebarImportSpecs = {
  fabrics?: SpecsCatalogFabric[];
} | null;

function getFabricColorValue(color: SpecsCatalogFabricColor) {
  return color.generatorHex || color.hex || "";
}

function isVisibleFabricColor(
  color: SpecsCatalogFabricColor
): color is SidebarFabricColor {
  return !color.disabled && !!color.name && !!getFabricColorValue(color);
}

function getLayerFabricColors(
  fabricNames: string[],
  importSpecs: SidebarImportSpecs
): SidebarFabricColor[] {
  const fabrics = importSpecs?.fabrics;
  if (fabricNames.length === 0 || !fabrics) {
    return [];
  }

  return fabricNames.flatMap((fabricName: string) => {
    const fabric = fabrics.find((candidate) => candidate.name === fabricName);
    return fabric ? (fabric.colors ?? []).filter(isVisibleFabricColor) : [];
  });
}

function createFabricEditorHref(fabricName: string) {
  const params = new URLSearchParams({ spec: fabricName });
  return `${ROUTES.SPECS_FABRICS}?${params.toString()}`;
}

interface SidebarFabricEditorProps {
  layer: SpecsLayer;
  modelLayer: LayerModel | null;
  importSpecs: SidebarImportSpecs;
  canInherit: boolean;
  updateLayerColor: (layerId: string, color: string) => void;
}

export default function SidebarFabricEditor({
  layer,
  modelLayer,
  importSpecs,
  canInherit,
  updateLayerColor,
}: SidebarFabricEditorProps) {
  const importFabrics =
    importSpecs?.fabrics
      ?.map((fabric) => fabric.name)
      .filter((name): name is string => !!name) ?? [];
  const layerFabricNames = modelLayer?.fabricNames ?? [];
  const fabricOptions = Array.from(
    new Set([...importFabrics, ...layerFabricNames])
  ).sort();
  const colors = getLayerFabricColors(layerFabricNames, importSpecs);

  if (fabricOptions.length === 0 && colors.length === 0 && !canInherit) {
    return null;
  }

  return (
    <Box sx={{ mb: "10px" }}>
      {fabricOptions.length > 0 && (
        <Box sx={{ mt: 0.5, mb: 0.5 }}>
          <Autocomplete
            size="small"
            multiple
            options={fabricOptions}
            value={layerFabricNames}
            onChange={(_e, newValue) => {
              modelLayer?.setFabricNames(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select fabric..."
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "11px",
                    minHeight: 28,
                  },
                  "& .MuiChip-root": {
                    height: 18,
                    fontSize: "10px",
                  },
                }}
              />
            )}
            disableCloseOnSelect
          />
          {layerFabricNames.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
              {layerFabricNames.map((fabricName) => (
                <Button
                  key={fabricName}
                  component="a"
                  href={createFabricEditorHref(fabricName)}
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    minWidth: 0,
                    px: 0.5,
                    py: 0,
                    fontSize: "10px",
                    lineHeight: 1.4,
                    textTransform: "none",
                  }}
                >
                  Open {fabricName}
                </Button>
              ))}
            </Stack>
          ) : null}
        </Box>
      )}

      {(colors.length > 0 || canInherit) && (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {colors.map((color) => (
            <Tooltip
              key={getFabricColorValue(color) + color.name}
              title={color.label || color.name}
            >
              <Box
                onClick={() =>
                  updateLayerColor(layer.id, getFabricColorValue(color))
                }
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: getFabricColorValue(color),
                  border:
                    layer.baseColor === color.hex ||
                    layer.baseColor === color.generatorHex
                      ? `2px solid ${palette.brand.primary}`
                      : "1px solid rgba(0,0,0,0.15)",
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "transform 0.1s",
                  "&:hover": { transform: "scale(1.2)" },
                }}
              />
            </Tooltip>
          ))}
          {canInherit ? (
            <InheritedColorSwatch
              selected={LayerModel.isInheritedColor(layer.baseColor)}
              onClick={() =>
                updateLayerColor(layer.id, LayerModel.INHERITED_COLOR_NAME)
              }
            />
          ) : null}
        </Stack>
      )}
    </Box>
  );
}

function InheritedColorSwatch({
  selected,
  onClick,
}: {
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip title={INHERITED_COLOR_LABEL}>
      <Box
        onClick={onClick}
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          bgcolor: "grey.100",
          border: selected
            ? `2px solid ${palette.brand.primary}`
            : "1px dashed rgba(0,0,0,0.45)",
          cursor: "pointer",
          flexShrink: 0,
          transition: "transform 0.1s",
          "&:hover": { transform: "scale(1.2)" },
        }}
      />
    </Tooltip>
  );
}
